// pages/ChatbotSettings.tsx
import React, { useState } from 'react';
import { MessageCircle, Code, Settings, Eye, Copy, Check, BarChart3, FileText, Puzzle, Palette } from 'lucide-react';
import { ChatbotTemplates } from '../modules/chatbot/components/ChatbotTemplates';
import { ChatbotIntegrations } from '../components/ChatbotIntegrations';
import { ChatbotStatistics } from '../modules/chatbot/components/ChatbotStatistics';
import { ChatbotCustomization } from '../modules/chatbot/components/ChatbotCustomization';
import { ChatbotAPISettings } from '../modules/chatbot/components/ChatbotAPISettings';

export const ChatbotSettings: React.FC = () => {
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<'embed' | 'api' | 'templates' | 'analytics' | 'integrations' | 'statistics' | 'customization'>('embed');
  const [config, setConfig] = useState({
    websiteId: 'default',
    position: 'bottom-right',
    primaryColor: '#FF6B00',
    title: 'Potrzebujesz pomocy?',
    welcomeMessage: 'Cześć! 👋 Jak mogę Ci pomóc?',
    autoShow: true,
    delay: 3000
  });

  const embedCode = `<!-- PlexiSystem Chatbot -->
<script>
  window.PlexiChatConfig = {
    websiteId: '${config.websiteId}',
    position: '${config.position}',
    primaryColor: '${config.primaryColor}',
    title: '${config.title}',
    welcomeMessage: '${config.welcomeMessage}',
    autoShow: ${config.autoShow},
    delay: ${config.delay}
  };
</script>
<script src="https://app.plexisystem.pl/chatbot-widget.js" async></script>
<!-- End PlexiSystem Chatbot -->`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold flex items-center gap-3 mb-2">
          <MessageCircle className="w-8 h-8 text-orange-500" />
          Ustawienia Chatbota AI
        </h1>
        <p className="text-gray-400">
          Skonfiguruj i zainstaluj czatbota na swojej stronie internetowej
        </p>
      </div>

      {/* Zakładki */}
      <div className="flex gap-2 mb-6 border-b border-zinc-700">
        <button
          onClick={() => setActiveTab('embed')}
          className={`px-4 py-2 font-medium transition-all ${
            activeTab === 'embed'
              ? 'text-orange-500 border-b-2 border-orange-500'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Code className="w-4 h-4 inline mr-2" />
          Instalacja
        </button>
        <button
          onClick={() => setActiveTab('api')}
          className={`px-4 py-2 font-medium transition-all ${
            activeTab === 'api'
              ? 'text-orange-500 border-b-2 border-orange-500'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Settings className="w-4 h-4 inline mr-2" />
          API Settings
        </button>
        <button
          onClick={() => setActiveTab('integrations')}
          className={`px-4 py-2 font-medium transition-all ${
            activeTab === 'integrations'
              ? 'text-orange-500 border-b-2 border-orange-500'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Puzzle className="w-4 h-4 inline mr-2" />
          Integracje
        </button>
        <button
          onClick={() => setActiveTab('templates')}
          className={`px-4 py-2 font-medium transition-all ${
            activeTab === 'templates'
              ? 'text-orange-500 border-b-2 border-orange-500'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <FileText className="w-4 h-4 inline mr-2" />
          Szablony
        </button>
        <button
          onClick={() => setActiveTab('analytics')}
          className={`px-4 py-2 font-medium transition-all ${
            activeTab === 'analytics'
              ? 'text-orange-500 border-b-2 border-orange-500'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <BarChart3 className="w-4 h-4 inline mr-2" />
          Analityka
        </button>
        <button
          onClick={() => setActiveTab('statistics')}
          className={`px-4 py-2 font-medium transition-all ${
            activeTab === 'statistics'
              ? 'text-orange-500 border-b-2 border-orange-500'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <BarChart3 className="w-4 h-4 inline mr-2" />
          Statystyki
        </button>
        <button
          onClick={() => setActiveTab('customization')}
          className={`px-4 py-2 font-medium transition-all ${
            activeTab === 'customization'
              ? 'text-orange-500 border-b-2 border-orange-500'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Palette className="w-4 h-4 inline mr-2" />
          Personalizacja
        </button>
      </div>

      {/* Zawartość zakładek */}
      {activeTab === 'embed' && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Konfiguracja */}
            <div className="bg-zinc-800 rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5 text-orange-500" />
                Konfiguracja
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    ID strony
                  </label>
                  <input
                    type="text"
                    value={config.websiteId}
                    onChange={(e) => setConfig({ ...config, websiteId: e.target.value })}
                    className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Pozycja
                  </label>
                  <select
                    value={config.position}
                    onChange={(e) => setConfig({ ...config, position: e.target.value })}
                    className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-white"
                  >
                    <option value="bottom-right">Prawy dolny róg</option>
                    <option value="bottom-left">Lewy dolny róg</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Kolor główny
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={config.primaryColor}
                      onChange={(e) => setConfig({ ...config, primaryColor: e.target.value })}
                      className="h-10 w-20"
                    />
                    <input
                      type="text"
                      value={config.primaryColor}
                      onChange={(e) => setConfig({ ...config, primaryColor: e.target.value })}
                      className="flex-1 px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Tytuł czatu
                  </label>
                  <input
                    type="text"
                    value={config.title}
                    onChange={(e) => setConfig({ ...config, title: e.target.value })}
                    className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Wiadomość powitalna
                  </label>
                  <textarea
                    value={config.welcomeMessage}
                    onChange={(e) => setConfig({ ...config, welcomeMessage: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-white"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="autoShow"
                    checked={config.autoShow}
                    onChange={(e) => setConfig({ ...config, autoShow: e.target.checked })}
                    className="w-4 h-4 text-orange-600"
                  />
                  <label htmlFor="autoShow" className="text-sm font-medium text-gray-300">
                    Pokaż automatycznie po załadowaniu strony
                  </label>
                </div>

                {config.autoShow && (
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Opóźnienie (ms)
                    </label>
                    <input
                      type="number"
                      value={config.delay}
                      onChange={(e) => setConfig({ ...config, delay: parseInt(e.target.value) || 3000 })}
                      className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-white"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Kod do wklejenia */}
            <div className="bg-zinc-800 rounded-lg shadow-lg p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Code className="w-5 h-5 text-orange-500" />
                Kod do instalacji
              </h2>

              <div className="mb-4 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <p className="text-sm text-yellow-400">
                  ⚠️ <strong>Uwaga:</strong> To jest przykładowy kod embed. Aby chatbot działał na zewnętrznej stronie, 
                  potrzebny jest dedykowany plik widget.js hostowany na serwerze PlexiSystem.
                </p>
              </div>

              <p className="text-sm text-gray-400 mb-4">
                Wklej poniższy kod przed tagiem &lt;/body&gt; na swojej stronie:
              </p>

              <div className="relative">
                <pre className="bg-zinc-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                  <code>{embedCode}</code>
                </pre>
                <button
                  onClick={copyToClipboard}
                  className="absolute top-2 right-2 bg-zinc-700 hover:bg-zinc-600 text-white p-2 rounded-lg transition-colors"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>

              <div className="mt-6">
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  Podgląd
                </h3>
                <div className="bg-zinc-700 rounded-lg p-4 text-center">
                  <p className="text-gray-400 mb-4">
                    Przycisk czatu pojawi się w wybranym rogu ekranu
                  </p>
                  <div className={`inline-block relative ${config.position === 'bottom-right' ? 'ml-auto' : 'mr-auto'}`}>
                    <button
                      className="text-white rounded-full p-4 shadow-lg"
                      style={{ backgroundColor: config.primaryColor }}
                    >
                      <MessageCircle className="w-6 h-6" />
                    </button>
                    {config.autoShow && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold animate-pulse">
                        1
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <h4 className="text-sm font-semibold text-blue-400 mb-2">💡 Aktualnie dostępne:</h4>
                <ul className="text-sm text-gray-300 space-y-1">
                  <li>• Testowanie chatbota: <a href="/test-chatbot" className="text-blue-400 hover:underline">app.plexisystem.pl/test-chatbot</a></li>
                  <li>• Widget wbudowany w aplikację PlexiSystem</li>
                  <li>• Integracja przez API dla developerów</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Statystyki */}
          <div className="mt-8 bg-zinc-800 rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-4">Statystyki czatbota</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-blue-500/10 rounded-lg p-4">
                <p className="text-sm text-gray-400">Aktywne konwersacje</p>
                <p className="text-2xl font-bold text-blue-400">12</p>
              </div>
              <div className="bg-green-500/10 rounded-lg p-4">
                <p className="text-sm text-gray-400">Zakończone dzisiaj</p>
                <p className="text-2xl font-bold text-green-400">45</p>
              </div>
              <div className="bg-purple-500/10 rounded-lg p-4">
                <p className="text-sm text-gray-400">Nowe leady</p>
                <p className="text-2xl font-bold text-purple-400">8</p>
              </div>
              <div className="bg-orange-500/10 rounded-lg p-4">
                <p className="text-sm text-gray-400">Średni czas rozmowy</p>
                <p className="text-2xl font-bold text-orange-400">4:32</p>
              </div>
            </div>
          </div>

          {/* Instrukcje */}
          <div className="mt-8 bg-orange-500/10 border border-orange-500/20 rounded-lg p-6">
            <h3 className="font-semibold mb-2">🚀 Jak to działa?</h3>
            <ol className="list-decimal list-inside space-y-2 text-sm text-gray-300">
              <li>Skonfiguruj ustawienia czatbota według swoich potrzeb</li>
              <li>Skopiuj wygenerowany kod JavaScript</li>
              <li>Wklej kod przed tagiem &lt;/body&gt; na swojej stronie</li>
              <li>Czatbot automatycznie pojawi się na stronie</li>
              <li>Wszystkie konwersacje i leady będą zapisywane w systemie PlexiSystem</li>
            </ol>
          </div>
        </>
      )}

      {activeTab === 'templates' && (
        <ChatbotTemplates />
      )}

      {activeTab === 'analytics' && (
        <div className="text-center py-12 bg-zinc-800 rounded-lg">
          <BarChart3 className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400 mb-6">Przejdź do pełnej analityki czatbota</p>
          <a
            href="/chatbot-analytics"
            className="inline-block bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg transition-colors"
          >
            Otwórz analitykę
          </a>
        </div>
      )}

      {activeTab === 'integrations' && (
        <ChatbotIntegrations />
      )}

      {activeTab === 'statistics' && (
        <ChatbotStatistics />
      )}

      {activeTab === 'customization' && (
        <ChatbotCustomization />
      )}

      {activeTab === 'api' && (
        <ChatbotAPISettings />
      )}
    </div>
  );
};